import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Building2, ChevronRight } from 'lucide-react';
import T from 'Components/Helpers/translate';
import NotificationService from 'App/babel/NotificationService';
import { useSoftDeployAccess } from 'App/components/hooks';
import { usePortalContainer } from 'App/shadcn/lib/portal-context';
import { PROSPECTING_TOOLS_FLAG } from '../ProspectingTools/tabs';
import DossierDrawer, { type DrawerCandidate } from '../ProspectingWorkspace/DossierDrawer';
import { LegacyStyles } from '../ProspectingWorkspace/LegacyEmbed';
import type { RunCandidateHandlers } from '../ProspectingAgent/RunDetailView';
import {
	fetchRunCandidates,
	fetchRunIsScored,
	importRunCandidates,
	markImportedWith,
	syncRunCandidate
} from '../ProspectingAgent/runCandidatesApi';

/**
 * One card per company a prospecting run researched, under the message that
 * reports the run (WEB-14734). A card opens the company's dossier - summary,
 * key figures, business signals and decision makers - in the same drawer the
 * prospecting workspace uses, so CRM accounts read like new prospects do.
 *
 * Read from `metadata.prospectingRunIds` (WEB-14733), so the cards survive a
 * reload; the companies come from the run-candidates read.
 */

/** The runs a message reports, from its metadata. */
export const readProspectingRunIds = (metadata: Record<string, unknown> | undefined): number[] => {
	const raw = metadata?.prospectingRunIds;
	return Array.isArray(raw) ? raw.filter((id): id is number => Number.isInteger(id) && id > 0) : [];
};

/**
 * A screen that already hosts the dossier drawer - Prospecting › Search - shows the cards'
 * companies in it, rather than the cards stacking a second drawer over it. `openProspectingId`
 * is the company it shows, so an import or sync from the cards can hand it the rewritten one.
 */
export type ResearchedCompaniesHost = {
	openCompany: (candidate: DrawerCandidate, handlers: RunCandidateHandlers, runId: number) => void;
	openProspectingId: string | null;
};
export const ResearchedCompaniesHostContext = createContext<ResearchedCompaniesHost | null>(null);

type Company = DrawerCandidate & {
	runId: number;
	city?: string;
	contacts?: unknown[];
	newsSignals?: unknown[];
};

const count = (list: unknown[] | undefined) => (Array.isArray(list) ? list.length : 0);

/** A run that skipped scoring stored 0 for every company; dropped, the dossier shows no "ICP 0/100" verdict. */
async function readRun(runId: number): Promise<Company[]> {
	const [list, scored] = await Promise.all([
		fetchRunCandidates<Company>(runId),
		fetchRunIsScored(runId).catch(err => {
			// The cards still show; at worst with the stored score, as before this read.
			console.error('[ResearchedCompanies] reading whether the run was scored failed', err);
			return true;
		})
	]);
	return list.map(c => ({ ...c, runId, ...(scored ? {} : { totalScore: undefined }) }));
}

/**
 * A company researched by two of the runs is one card, from the first run that has it. A run that
 * cannot be read leaves out its own cards, not the others'; only when none can is it a failure.
 */
async function loadCompanies(runIds: number[]): Promise<Company[]> {
	const perRun = await Promise.allSettled(runIds.map(readRun));
	const failures = perRun.flatMap(read => (read.status === 'rejected' ? [read.reason] : []));
	if (failures.length === perRun.length) throw failures[0];
	for (const reason of failures) console.error("[ResearchedCompanies] loading one run's candidates failed", reason);
	const seen = new Set<string>();
	return perRun
		.flatMap(read => (read.status === 'fulfilled' ? read.value : []))
		.filter(c => {
			if (!c.prospectingId || seen.has(c.prospectingId)) return false;
			seen.add(c.prospectingId);
			return true;
		});
}

const CompanyCard = ({ company, onOpen }: { company: Company; onOpen: () => void }) => {
	const decisionMakers = count(company.contacts);
	const signals = count(company.newsSignals);
	const hints = [
		company.city,
		decisionMakers === 1
			? T('assistant.researchedCompanies.decisionMakersOne')
			: decisionMakers
			? T('assistant.researchedCompanies.decisionMakers').replace('{n}', String(decisionMakers))
			: null,
		signals === 1
			? T('assistant.researchedCompanies.signalsOne')
			: signals
			? T('assistant.researchedCompanies.signals').replace('{n}', String(signals))
			: null
	].filter(Boolean);

	return (
		<li>
			<button
				type="button"
				onClick={onOpen}
				className="flex w-full min-w-0 items-center gap-2.5 rounded-[6px] border border-grey-4 bg-background px-3 py-2 text-left hover:bg-grey-1"
			>
				<Building2 size={14} className="shrink-0 text-grey-10" aria-hidden />
				<span className="flex min-w-0 flex-1 flex-col">
					<span className="truncate text-[13px] font-medium text-black">
						{company.name || company.prospectingId}
					</span>
					{hints.length ? (
						<span className="truncate text-[12px] text-grey-10">{hints.join(' · ')}</span>
					) : null}
				</span>
				<ChevronRight size={14} className="shrink-0 text-grey-8" aria-hidden />
			</button>
		</li>
	);
};

const CompanyCards = ({ runIds }: { runIds: number[] }) => {
	const [companies, setCompanies] = useState<Company[] | null>(null);
	const [failed, setFailed] = useState(false);
	const [openId, setOpenId] = useState<string | null>(null);
	const host = useContext(ResearchedCompaniesHostContext);
	const portalContainer = usePortalContainer();
	const runKey = runIds.join(',');

	useEffect(() => {
		let cancelled = false;
		setFailed(false);
		loadCompanies(runIds)
			.then(list => {
				if (!cancelled) setCompanies(list);
			})
			.catch(err => {
				console.error('[ResearchedCompanies] loading the run candidates failed', err);
				if (!cancelled) setFailed(true);
			});
		return () => {
			cancelled = true;
		};
		// runKey stands in for runIds, which a re-render hands over as a new array.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [runKey]);

	// Looked up on every render, so an import or sync re-renders the open dossier as in the CRM.
	const open = openId ? companies?.find(c => c.prospectingId === openId) : undefined;
	const close = useCallback(() => setOpenId(null), []);

	const importInto = (runId: number, prospectingIds: string[], contactNames?: string[]) => {
		importRunCandidates(runId, prospectingIds, contactNames)
			.then(importedById => {
				const markImported = markImportedWith(importedById);
				setCompanies(prev => prev?.map(c => (c.runId === runId ? markImported(c) : c)) ?? prev);
			})
			.catch(err => {
				console.error('[ResearchedCompanies] import failed', err);
				// The chat has no list of its own to show the failure in.
				NotificationService.add({
					icon: 'times',
					style: NotificationService.style.ERROR,
					title: T('assistant.researchedCompanies.importFailed')
				});
			});
	};

	const syncFrom = async (
		runId: number,
		prospectingId: string,
		clientId: number,
		mode: 'sync' | 'new',
		contactNames?: string[]
	) => {
		// A failed sync rejects to the dossier, which tells the rep.
		const result = await syncRunCandidate(runId, prospectingId, clientId, mode, contactNames);
		// The sync went through; failing to re-read it must not report it as failed.
		try {
			const fresh = await fetchRunCandidates<Company>(runId);
			const byId = new Map(fresh.map(c => [c.prospectingId, c]));
			setCompanies(
				prev =>
					prev?.map(c => {
						const next = c.runId === runId ? byId.get(c.prospectingId) : undefined;
						// A sync rescores nothing: the score, or its absence, stays as read.
						return next ? { ...next, runId, totalScore: c.totalScore } : c;
					}) ?? prev
			);
		} catch (err) {
			console.error('[ResearchedCompanies] re-reading the run after a sync failed', err);
		}
		return result;
	};

	const handlersFor = useCallback(
		(runId: number): RunCandidateHandlers => ({
			onImport: (prospectingIds, contactNames) => importInto(runId, prospectingIds, contactNames),
			onSync: (prospectingId, clientId, mode, contactNames) =>
				syncFrom(runId, prospectingId, clientId, mode, contactNames)
		}),
		// importInto and syncFrom only write through the state setter.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	// An import or sync rewrites the company; the host's drawer follows it, but only for a company
	// opened from these cards - the same company may be open there from a run's own list.
	const handedTo = useRef<string | null>(null);
	const handedOver = useRef<Company | undefined>(undefined);
	const hostOpen =
		host?.openProspectingId && host.openProspectingId === handedTo.current
			? companies?.find(c => c.prospectingId === host.openProspectingId)
			: undefined;
	useEffect(() => {
		if (!host || !hostOpen || handedOver.current === hostOpen) return;
		handedOver.current = hostOpen;
		host.openCompany(hostOpen, handlersFor(hostOpen.runId), hostOpen.runId);
	}, [host, hostOpen, handlersFor]);

	const openCard = (company: Company) => {
		if (!host) {
			setOpenId(company.prospectingId);
			return;
		}
		handedTo.current = company.prospectingId;
		handedOver.current = company;
		host.openCompany(company, handlersFor(company.runId), company.runId);
	};

	// Esc closes the dossier and nothing else. The hosts close themselves on Esc from a window
	// listener - the floating chat, and the AI Workspace overlay, which can't see this dialog in
	// the shadow root - so this answers first, from the document, after any popover in the
	// dossier has had its turn.
	useEffect(() => {
		if (!openId) return;
		const onKey = (event: KeyboardEvent) => {
			if (event.key !== 'Escape' || event.defaultPrevented) return;
			event.preventDefault();
			event.stopPropagation();
			close();
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [openId, close]);

	if (failed) {
		return <p className="m-0 pl-14 text-[12px] text-grey-10">{T('assistant.researchedCompanies.loadFailed')}</p>;
	}
	if (!companies?.length) return null;

	return (
		<section aria-label={T('assistant.researchedCompanies.title')} className="pl-14 pr-2">
			<h4 className="m-0 mb-1.5 font-mono text-[10px] uppercase tracking-[.08em] text-grey-10">
				{T('prospectingAgent.nCompanies').replace('{n}', String(companies.length))}
			</h4>
			<ul className="m-0 grid list-none gap-1.5 p-0">
				{companies.map(company => (
					<CompanyCard key={company.prospectingId} company={company} onOpen={() => openCard(company)} />
				))}
			</ul>
			{open
				? createPortal(
						// The drawer places itself in its container. Portalled to the scope's
						// root, that is the scope's own box - the page under the navbar - as
						// in the prospecting workspace, not the scrolling thread.
						<div className="absolute inset-0 z-[1000]">
							<LegacyStyles />
							<DossierDrawer
								candidate={open}
								left={0}
								onClose={close}
								onImport={handlersFor(open.runId).onImport}
								onSync={handlersFor(open.runId).onSync}
							/>
						</div>,
						portalContainer ?? document.body
				  )
				: null}
		</section>
	);
};

const ResearchedCompanies = ({ metadata }: { metadata: Record<string, unknown> | undefined }) => {
	const enabled = useSoftDeployAccess(PROSPECTING_TOOLS_FLAG);
	const runIds = readProspectingRunIds(metadata);
	return enabled && runIds.length ? <CompanyCards runIds={runIds} /> : null;
};

export default ResearchedCompanies;
